import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import { fetchWithAuth, isAdminUser } from '../../../utils/api';
import { useAccess, Capability, type CapabilityValue } from '../../../hooks/useAccess';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';

interface SettingsNavItem {
  name: string;
  path: string;
  adminOnly?: boolean;
  /** When set, the item shows only if the user holds this RBAC capability
   * (takes precedence over adminOnly — capability-driven gating). */
  capability?: CapabilityValue;
  /** Org-level surface that must NOT ride the "platform admin sees everything"
   * shortcut — a platform admin in global scope has no org context for it, so
   * it shows only when `capability` is actually held (an org admin, or a
   * platform admin ACTING as an org). Used for Teams. */
  orgContextOnly?: boolean;
}

interface SettingsNavSection {
  title: string;
  items: SettingsNavItem[];
}

interface ProviderKeyStatus {
  provider: 'openai';
  label: string;
  configured: boolean;
  masked_value: string | null;
  validation_status: 'valid' | 'invalid' | 'unknown';
  last_validated_at: string | null;
  last_validation_error: string | null;
  updated_at: string | null;
}

const INVALID_PROVIDER_BADGE_PATHS = new Set([
  '/v3/settings/billing/provider-keys',
  '/v3/settings/billing/usage',
]);

const PROVIDER_KEY_STATUS_EVENT = 'billing-provider-key-status-updated';

const V3SettingsLayout: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { can } = useAccess();
  // Provider-key/billing status is a platform-internal control (the endpoint
  // requires platform.manage), so load it on that capability — not the legacy
  // admin role, which an org admin also carries but which can't read it.
  const canReadPlatformBilling = can(Capability.PlatformManage);
  const [providerKeyStatus, setProviderKeyStatus] = useState<ProviderKeyStatus | null>(null);

  useEffect(() => {
    if (!canReadPlatformBilling) {
      setProviderKeyStatus(null);
      return;
    }

    let cancelled = false;
    const loadProviderKeyStatus = async () => {
      try {
        const response = (await fetchWithAuth('/v1/billing/provider-keys')) as ProviderKeyStatus[];
        const openaiStatus =
          response.find((item) => item.provider === 'openai') ?? null;
        if (!cancelled) {
          setProviderKeyStatus(openaiStatus);
        }
      } catch (error) {
        console.error('Failed to load provider key status for settings layout', error);
      }
    };

    const handleProviderStatusUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ProviderKeyStatus | null>;
      setProviderKeyStatus(customEvent.detail ?? null);
    };

    void loadProviderKeyStatus();
    window.addEventListener(PROVIDER_KEY_STATUS_EVENT, handleProviderStatusUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener(PROVIDER_KEY_STATUS_EVENT, handleProviderStatusUpdate);
    };
  }, [canReadPlatformBilling]);

  const hasInvalidProviderKey =
    providerKeyStatus?.configured === true && providerKeyStatus?.validation_status === 'invalid';

  const settingsSections: SettingsNavSection[] = useMemo(
    () =>
      ([
        {
          title: 'Settings',
          items: [
            { name: 'Features and Visibility', path: '/v3/settings/features-visibility', capability: Capability.PlatformManage },
            { name: 'Display Settings', path: '/v3/settings/display-settings', capability: Capability.PlatformManage },
            { name: 'Task Queue', path: '/v3/settings/task-queue', capability: Capability.PlatformManage },
            { name: 'Scheduled Jobs', path: '/v3/settings/scheduled-jobs', capability: Capability.PlatformManage },
            { name: 'Monitor Runs', path: '/v3/settings/import-jobs', capability: Capability.PlatformManage },
          ],
        },
        {
          title: 'Organization',
          items: [
            { name: 'Details', path: '/v3/settings/organization' },
            { name: 'Users & Permissions', path: '/v3/settings/users', capability: Capability.FirmManage, orgContextOnly: true },
            { name: 'Teams', path: '/v3/settings/teams', capability: Capability.FirmManage, orgContextOnly: true },
            { name: 'Features', path: '/v3/settings/rbac-features', capability: Capability.PlatformManage },
            { name: 'Audit Trail', path: '/v3/settings/audit-trail', capability: Capability.FirmAuditView, orgContextOnly: true },
            { name: 'API Keys', path: '/v3/settings/api-keys', capability: Capability.PlatformManage },
            { name: 'Notifications', path: '/v3/settings/notifications', capability: Capability.PlatformManage },
          ],
        },
        {
          title: 'Billing',
          items: [
            { name: 'Provider Keys', path: '/v3/settings/billing/provider-keys', capability: Capability.PlatformManage },
            { name: 'Usage Analytics', path: '/v3/settings/billing/usage', capability: Capability.PlatformManage },
          ],
        },
        {
          title: 'Data',
          items: [
            { name: 'Connectors', path: '/v3/settings/connectors', capability: Capability.PlatformManage },
            { name: 'Import History', path: '/v3/settings/imports', capability: Capability.PlatformManage },
          ],
        },
        {
          title: 'Intelligence',
          items: [
            { name: 'Skills', path: '/v3/settings/skills', capability: Capability.PlatformManage },
            { name: 'FOS Corpus', path: '/v3/settings/fos', capability: Capability.PlatformManage },
            { name: 'Ombudsman Decisions', path: '/v3/settings/ombudsman', capability: Capability.PlatformManage },
            { name: 'Taxonomy', path: '/v3/settings/taxonomy', capability: Capability.PlatformManage },
          ],
        },
      ] as SettingsNavSection[])
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            // Teams (and any orgContextOnly item) is an org-level concept: a
            // platform admin in GLOBAL scope has no team notion, so it must NOT
            // ride the platform-sees-all shortcut — it shows only when the
            // firm capability is actually held (an org admin, or a platform
            // admin ACTING as an org, whose synthetic profile grants it).
            if (item.orgContextOnly) return item.capability ? can(item.capability) : false;
            // Sep-3: a platform administrator sees every other settings surface;
            // organization users see only what their capability grants — and
            // internal items are PlatformManage-tagged, so they never appear.
            if (can(Capability.PlatformManage)) return true;
            if (item.capability) return can(item.capability);
            return !item.adminOnly;
          }),
        }))
        .filter((section) => section.items.length > 0),
    // `can` is included so the memo recomputes once /rbac/me resolves — it's
    // rebuilt each render from the latest capabilities. Without it, items gated
    // on `can(...)` (Firm Users/Teams/Roles/Audit), filtered out on the first
    // render before caps load, would never reappear: `isAdmin` (legacy role)
    // never changes for a FIRM_ADMIN, so the memo would hold its stale value.
    [isAdmin, can],
  );

  // Below lg the menu stacks above the content, so the whole panel scrolls and the
  // content keeps its own height: the card clips to its corners without becoming a
  // scroll container, which would collapse it, and wide content scrolls sideways
  // inside it. From lg the content scrolls beside the menu.
  return (
    <div className="flex h-full flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 md:px-6 md:py-5 lg:overflow-hidden xl:px-8 xl:py-6">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Settings
        </p>
        <h1 className="mt-1 font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
          Workspace configuration
        </h1>
        <p className="mt-2 max-w-2xl text-[13px] text-muted-foreground">
          Manage your organization's users, roles, and teams, and review the audit trail.
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-5 lg:min-h-0 lg:flex-row">
        <aside className="w-full shrink-0 rounded-[24px] border border-border bg-background shadow-[var(--shadow-card)] lg:w-72">
          <nav className="flex max-h-full flex-col gap-6 overflow-y-auto p-4">
            {settingsSections.map((section) => (
              <div key={section.title}>
                <div className="mb-2 px-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    {section.title}
                  </span>
                </div>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={
                          isActive
                            ? 'flex items-center rounded-2xl border border-primary/15 bg-[var(--go-good-vibes-tint)] px-3 py-2.5 text-[13px] font-semibold text-primary shadow-[var(--shadow-card)]'
                            : 'flex items-center rounded-2xl border border-transparent px-3 py-2.5 text-[13px] font-medium text-muted-foreground transition-[background-color,color,border-color,box-shadow] hover:border-primary/10 hover:bg-accent hover:text-foreground'
                        }
                      >
                        <span>{item.name}</span>
                        {hasInvalidProviderKey && INVALID_PROVIDER_BADGE_PATHS.has(item.path) ? (
                          <Badge
                            variant="attention"
                            className="ml-auto border-0 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]"
                          >
                            Alert
                          </Badge>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1 rounded-[28px] border border-border bg-background shadow-[var(--shadow-card)] overflow-clip lg:min-h-0 lg:overflow-hidden">
          <div className="overflow-x-auto lg:h-full lg:overflow-auto">
            {hasInvalidProviderKey ? (
              <div className="border-b border-border/70 px-4 py-4 md:px-6">
                <Alert className="border-rose-200 bg-rose-50 text-rose-900">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Organization OpenAI key needs attention</AlertTitle>
                  <AlertDescription className="text-rose-800">
                    <p>
                      Organization-scoped LLM calls will fail until the stored provider key is
                      fixed or removed.
                    </p>
                    {providerKeyStatus?.last_validation_error ? (
                      <p>{providerKeyStatus.last_validation_error}</p>
                    ) : null}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default V3SettingsLayout;
